import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    FavoriteBorder,
    ErrorOutline,
    Gavel,
    TipsAndUpdates,
    DoneOutline,
    Group,
    Home,
    FormatListNumbered,
    MeetingRoom
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 }, pb: 6 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    1.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('introduction')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    ルームシェアで「恋人同伴」問題が起こる理由
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    2.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('trouble-cases')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    実際にあったトラブル事例とその背景
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    3.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('rules')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    ルール作りで押さえるべきポイント
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    4.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('how-to-agree')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    みんなが納得するルールの決め方
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    5.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('expert-advice')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    専門家の視点からアドバイス
                                </Link>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアで「恋人同伴」問題が起こる理由
                </Typography>
                <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                    ルームシェアは、コストを抑えながら広々とした空間で暮らせる魅力的な住まい方です。しかし、共同生活だからこそ、プライベートな話題で摩擦が生じやすいのも事実。その代表的なものが、「恋人の宿泊」問題です。
                </Typography>
                <Typography variant="body1" gutterBottom>
                    <b>「自分の部屋に恋人を呼びたい」</b>という気持ちと、<b>「他人が頻繁に出入りするのはちょっと…」</b>という感覚。このギャップが、思わぬトラブルに発展することも。<br />
                    本記事では、ルームシェア経験者からよく寄せられる「恋人同伴」問題について、トラブル事例や円満なルール作りのコツ、納得できる話し合いの方法まで詳しく解説します。
                </Typography>

                <Typography variant="body2" color="textSecondary">
                    共同生活では、誰かの「当たり前」が他の人の「ストレス」になることも。恋人同伴問題はその典型です。
                </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* トラブル事例 */}
            <Box id="trouble-cases" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    実際にあったトラブル事例とその背景
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, minHeight: 180 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ErrorOutline sx={{ color: '#e57373', mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>事例1：頻繁な宿泊で「家賃不公平感」</Typography>
                            </Box>
                            <Typography variant="body2">
                                あるルームシェアでは、ルームメイトが毎週末恋人を泊め、リビングやバスルームも頻繁に利用。<br />
                                「恋人も家賃を負担すべきでは？」という声が上がり、関係がギクシャク…。<br />
                                <Chip label="家賃・光熱費の不公平感" color="primary" variant="outlined" size="small" sx={{ mt: 1 }} />
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, minHeight: 180 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ErrorOutline sx={{ color: '#e57373', mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>事例2：生活リズムの乱れとプライバシー問題</Typography>
                            </Box>
                            <Typography variant="body2">
                                夜遅くまで恋人と過ごす音や、共有スペースの長時間占有に他のメンバーがストレスを感じるケースも。<br />
                                「自分の家なのに気を遣う…」と、居心地の悪さからルームシェアが解消してしまった例もありました。
                                <Chip label="プライバシー・騒音" color="primary" variant="outlined" size="small" sx={{ mt: 1 }} />
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Paper elevation={0} sx={{ p: 3, mt: 2, bgcolor: '#fffde7', borderLeft: '6px solid #ffe082' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <b>なぜ起こる？</b>
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <Group color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="「家族」でも「完全な他人」でもない微妙な関係性"
                                secondary="共同生活ゆえの距離感の違いが生まれやすい"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Home color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="共有スペースの使い方への認識差"
                                secondary="共用設備を“家族”のように使う人と、“ホテル”感覚の人が混在"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <FavoriteBorder color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="恋人の存在は“外部者”か“ゲスト”か"
                                secondary="本人と他メンバーで意識の差が出やすい"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Box
                    component="img"
                    src="/images/blog/sharing-kitchen_roomshare-koibito-douhan-rule.jpg"
                    alt="広々としたモダンなシェアハウスのキッチンで、住人とその恋人が一緒に料理し、他の住人が少し気まずそうに見守っている様子。白を基調とした明るい空間。"
                    sx={{ width: '100%', borderRadius: 2, my: 3, boxShadow: 1 }}
                />
                <Typography variant="body2" color="textSecondary">
                    恋人同伴が日常化すると、共有スペースの雰囲気や人間関係にも影響が出やすくなります。
                </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* ルール作り */}
            <Box id="rules" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルール作りで押さえるべきポイント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    トラブルを未然に防ぐには、「恋人同伴」に関する納得度の高いルール作りが不可欠です。住人全員が安心して暮らせるための、実践的なポイントを紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 380, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Gavel sx={{ color: '#1976d2', mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                        ポイント1：明確なルールを文章化
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    「同伴OK/NG」「宿泊頻度の上限」「事前申請の要否」などは、あいまいにせず書面化するのがトラブル防止の基本です。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 380, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <FormatListNumbered sx={{ color: '#1976d2', mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                        ポイント2：具体的な数値や条件を設定
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    「月◯回まで」「1回の宿泊は◯泊まで」など、具体的な制限や、光熱費負担・ゲスト利用料の有無も明記しましょう。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 380, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <MeetingRoom sx={{ color: '#1976d2', mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                        ポイント3：共有スペース利用のマナーも盛り込む
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    深夜利用・長時間のリビング占有・シャワー利用時間など、細やかな配慮項目も忘れずに。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 380, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <DoneOutline sx={{ color: '#1976d2', mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                        ポイント4：全員の合意を得てからスタート
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    ルールは一方的に決めず、全員の納得を確認してから実施することが大切です。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    ルール例（参考）：
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemIcon>
                            <Chip label="例" color="primary" size="small" variant="outlined" />
                        </ListItemIcon>
                        <ListItemText primary="恋人の宿泊は月2回まで、1回につき2泊までとする" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <Chip label="例" color="primary" size="small" variant="outlined" />
                        </ListItemIcon>
                        <ListItemText primary="同伴時は必ず事前にグループチャットで申請する" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <Chip label="例" color="primary" size="small" variant="outlined" />
                        </ListItemIcon>
                        <ListItemText primary="ゲスト料金（光熱費分担）を1泊500円とする" />
                    </ListItem>
                </List>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* みんなが納得するルールの決め方 */}
            <Box id="how-to-agree" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    みんなが納得するルールの決め方
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルール作りで最も大切なのは、「自分たちの生活」に合った内容にすること。そのためには、全員の本音や価値観を引き出す対話が不可欠です。
                </Typography>
                <Box
                    component="img"
                    src="/images/blog/roomshare-meeting_roomshare-koibito-douhan-rule.jpg"
                    alt="ルームメイト同士が丸テーブルを囲み、ノートパソコンとメモを手に真剣に話し合っている様子。カラフルなクッションや観葉植物がある温かい雰囲気の共有リビング。"
                    sx={{ width: '100%', borderRadius: 2, my: 3, boxShadow: 1 }}
                />
                <List sx={{ mb: 2 }}>
                    <ListItem>
                        <ListItemIcon>
                            <TipsAndUpdates color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="全員で「困りごと」「理想」を率直に話す"
                            secondary="遠慮せず、どんな時に不快・不安を感じるかも含めて共有しましょう"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <TipsAndUpdates color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="「なぜそのルールが必要？」を説明し合う"
                            secondary="押し付けでなく、背景や理由を丁寧に伝えることで納得感が増します"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <TipsAndUpdates color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="運用後も定期的に見直す"
                            secondary="生活が変化した時は都度ルールをアップデート。柔軟な運用が長続きのコツです"
                        />
                    </ListItem>
                </List>
                <Paper elevation={1} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                        <b>POINT:</b> ルール作りは「みんなで暮らしを快適にするための知恵」を出し合う場。<br />
                        誰か1人が我慢する仕組みではなく、全員が気持ちよく暮らせる妥協点を探しましょう。
                    </Typography>
                </Paper>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* 専門家の視点からアドバイス */}
            <Box id="expert-advice" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    専門家の視点からアドバイス
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアを長く快適に続けている住人たちには、いくつかの共通した工夫があります。恋人同伴ルールをきっかけに、よりよいシェア生活を目指しましょう。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderLeft: '5px solid #1976d2', bgcolor: '#f5f5f5' }}>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', fontSize: '1rem' }}>
                                <DoneOutline sx={{ verticalAlign: 'middle', mr: 1, color: '#1976d2' }} />
                                トラブル回避のコツ
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <TipsAndUpdates color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="恋人同伴以外のゲストルールもまとめて決めておく"
                                        secondary="友人や家族の訪問も同様にルール化すると公平感が生まれます"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <TipsAndUpdates color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="「困った時は必ず話し合う」仕組みをつくる"
                                        secondary="匿名アンケートや定期ミーティングを設定すると安心感UP"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderLeft: '5px solid #43a047', bgcolor: '#f5f5f5' }}>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', fontSize: '1rem' }}>
                                <DoneOutline sx={{ verticalAlign: 'middle', mr: 1, color: '#43a047' }} />
                                快適なルームシェアのために
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <TipsAndUpdates color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="小さな不満も早めに言葉にする"
                                        secondary="「言いにくいな…」と思っても、ため込まず伝えることが長続きの秘訣"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <TipsAndUpdates color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="「ありがとう」を積極的に伝える"
                                        secondary="相手への感謝や気遣いが、雰囲気を良くする最大のコツ"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box
                    component="img"
                    src="/images/blog/sharehouse-happy_roomshare-koibito-douhan-rule.jpg"
                    alt="カジュアルな雰囲気のリビングで、複数のルームメイトが笑顔で乾杯しているシーン。多様な年代・性別の住人がリラックスし、和やかな空気が流れている。"
                    sx={{ width: '100%', borderRadius: 2, my: 3, boxShadow: 1 }}
                />
                <Typography variant="body2" color="textSecondary">
                    ルールは「縛り」ではなく「快適な暮らしを守るための道具」。みんなで創るからこそ、よりよいルームシェアが実現します。
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-koibito-douhan-rule',
    slug: 'roomshare-koibito-douhan-rule',
    title: 'ルームシェアでの「恋人」同伴はOK？ルールの作り方と注意点',
    excerpt: 'ルームシェアでよく起こる「恋人の宿泊」問題。実際のトラブル事例や円満なルール作りのコツ、みんなが納得できる話し合いの方法まで、専門的な視点で徹底解説。快適な共同生活のための実践的アドバイスを紹介します。',
    coverImage: '/images/blog/cover-roomshare-koibito_roomshare-koibito-douhan-rule.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。ウッド素材の家具や観葉植物が配置され、アットホームで温かな雰囲気が広がる空間が印象的。',
    publishedAt: '2024-06-06T00:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '暮らしの知恵', 'トラブル回避'],
    tags: ['ルームシェア', '恋人同伴', 'ルール作り', '共同生活', 'シェアハウス'],
    author: {
        name: '山本 未来',
        avatar: '/images/avatars/yamamoto-mirai.jpg',
        bio: 'ルームシェア歴10年以上。都内を中心に20軒以上のシェアハウス運営をサポートし、トラブル解決や快適な共同生活のためのアドバイスを多数手がける。メディア・書籍でも活躍中。',
        profilePicture: '/images/avatars/yamamoto-mirai.jpg'
    },
    viewCount: 1347,
    contentComponent: PostContent
};

export default post;